import React, { useEffect, useState } from 'react';

import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';

import { useCreateRovoExperienceSidebarController } from '@confluence/ai-rovo-button/entry-points/useCreateRovoExperienceSidebarController.ts';
import { expValEquals } from '@confluence/feature-experiments';

import {
	isRecentlyCopiedContentId,
	clearRecentlyCopiedContentId,
} from './recentlyCopiedContent.ts';
import { CopyWithRovoBannerCard } from './CopyWithRovoBannerCard.tsx';

type CopyWithRovoProps = {
	contentId: string;
};

/**
 * Checks whether the given content ID was recently created from a
 * copy/duplicate operation and applies the experiment cohort behaviour:
 *
 * - `banner`   – renders the banner card with a Keep Page Structure button
 * - `rovoChat` – opens the Rovo chat panel in sidebar mode
 * - `control`  – does nothing
 *
 * Clears localStorage regardless of what experience was shown.
 */
export const CopyWithRovo = ({ contentId }: CopyWithRovoProps) => {
	const sidebarApi = useCreateRovoExperienceSidebarController();
	const { toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();
	const [showBanner, setShowBanner] = useState(false);

	useEffect(() => {
		if (!isRecentlyCopiedContentId(contentId)) {
			return;
		}

		if (expValEquals('cc_fd_copy_with_rovo', 'cohort', 'banner')) {
			setShowBanner(true);
		} else if (expValEquals('cc_fd_copy_with_rovo', 'cohort', 'rovoChat')) {
			toggleConversationAssistantUIMode({
				targetMode: ConversationAssistantUIModes.SIDEBAR,
				sidebarApi,
			});
		}

		clearRecentlyCopiedContentId(contentId);
	}, [contentId, sidebarApi, toggleConversationAssistantUIMode]);

	if (!showBanner) {
		return null;
	}

	return <CopyWithRovoBannerCard contentId={contentId} />;
};
